import { useMemo } from 'react';
import { useFormatter } from 'js/hooks/useFormatter';

export const useFormatterPublicAPI = () => {
    const {
        date,
        getDate,
        getTimeLong,
        parseImsDate,
        getTimeShort,
        formatNumber,
        getDateForCsv,
        getDateForIms,
        formatCurrency,
        getDateTimeLong,
        delocalizeNumber,
        getDateTimeShort,
        getDateTimeForCsv,
        getDateTimeLongMs,
        getCurrencySymbol,
        getLocaleDateShort,
        parseImsBankIdDate,
        getDurationTimeLong,
        getDurationTimeShort,
        formatTimeFromSeconds,
        getIsCurrencySymbolAtTheLeft
    } = useFormatter();

    return useMemo(() => ({
        // region Currency

        /**
         * Formats amount in currency according to options.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {number|string} amount Amount to format.
         * @param {Object} [options] Formatter params.
         * @param {string} [options.locale] If not passed, user's locale is used.
         * @param {string} [options.currencyCode] Currency, if not passed, user's currency is used.
         * @param {string} [options.symbolAtTheLeft=true] 'true' to draw currency symbol to the left of amount.
         * @param {number} [options.precision=2] Decimal precision.
         * @param {string} [options.thSeparator=,] Thousand separator.
         * @param {string} [options.decSeparator=.] Decimal separator.
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // 'GB 45.00'
         * Playtech.API.formatter.formatCurrency(45, {currencyCode: "GB"});
         * @example
         * // '£ 45.00'
         * Playtech.API.formatter.formatCurrency(45, {}, 'de_DE');
         * @example
         * // '4:567*000 £'
         * Playtech.API.formatter.formatCurrency('4567', {symbolAtTheLeft: false, precision: 3, thSeparator: ':', decSeparator: '*'});
         */
        formatCurrency,
        /**
         * Gets currency symbol to display.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {string|Object} [options] Currency code or currency options, if not passed, user's currency is used.
         * @param {string} [options.currencyCode] Currency code
         * @returns {string}
         * @example
         * // '$'
         * Playtech.API.formatter.getCurrencySymbol({ currencyCode: "USD" });
         */
        getCurrencySymbol,
        /**
         * @memberOf Playtech.API.formatter
         * @function
         * @param {Object} [options] SymbolAtTheLeft options.
         * @param {string} [options.symbolAtTheLeft]
         * @param {string} [options.locale] If not passed, user's locale is used.
         * @returns {boolean}
         * @example
         * // true
         * Playtech.API.formatter.getIsCurrencySymbolAtTheLeft({currencyCode: "USD"});
         * @example
         * // false
         * Playtech.API.formatter.getIsCurrencySymbolAtTheLeft({symbolAtTheLeft: false});
         */
        getIsCurrencySymbolAtTheLeft,

        // endregion

        // region Date

        /**
         * Gets date in custom format.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {Date|string} srcDate
         * @param {string} [customFormat=date]
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '12-04-2022'
         * Playtech.API.formatter.date(new Date(2022, 03, 12), '%d-%m-%Y');
         * @example
         * // 'Wed-Jun-6/22/2022, 9:32:07 AM-20-22-06/22/22- 6-6th'
         * Playtech.API.formatter.date(new Date('Wed Jun 22 2022 09:32:07 GMT+0300 (Eastern European Summer Time)'), '%a-%b-%c-%C-%d-%D-%e-%E');
         * @example
         * // '09-09-172-06-32-AM-09:32:07 AM-09:32'
         * Playtech.API.formatter.date(new Date('Wed Jun 22 2022 09:32:07 GMT+0300 (Eastern European Summer Time)'), '%H-%I-%j-%m-%M-%p-%r-%R');
         * @example
         * // '07-000-09:32:07-6/22/2022-9:32:07 AM-22-2022--0180'
         * Playtech.API.formatter.date(new Date('Wed Jun 22 2022 09:32:07 GMT+0300 (Eastern European Summer Time)'), '%S-%SSS-%T-%x-%X-%y-%Y-%Z');
         */
        date,
        /**
         * Gets date in 'date' format e.g. YYYY-MM-DD Depends on Date/Time format setting in CMS.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {*|Date|string} srcDate
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '22/06/2022'
         * Playtech.API.formatter.getDate();
         * @example
         * // '19/08/2014'
         * Playtech.API.formatter.getDate('2014-08-19T18:59:21+02:00');
         */
        getDate,
        /**
         * Gets date in 'timeLong' format e.g HH:MM:SS Depends on Date/Time format setting in CMS.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {*|Date|string} srcDate
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '19/08/2014'
         * Playtech.API.formatter.getTimeLong('2014-08-19T18:59:21+02:00');
         * @example
         * // '12:06:30 PM'
         * Playtech.API.formatter.getTimeLong("2014-08-22T12:06:30.0200");
         */
        getTimeLong,
        /**
         * Gets date in 'timeShort' format e.g HH:MM Depends on Date/Time format setting in CMS.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {*|Date|string} srcDate
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '09:55 AM'
         * Playtech.API.formatter.getTimeShort(new Date('Wed Jun 22 2022 09:55:11 GMT+0300'));
         * @example
         * // '14:55'
         * Playtech.API.formatter.getTimeShort(new Date('Wed Jun 22 2022 14:55:11 GMT+0300'));
         */
        getTimeShort,
        /**
         * Gets date in 'dateTimeLong' format e.g YYYY-MM-DD HH:MM:SS Depends on Date/Time format setting in CMS.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {*|Date|string} srcDate
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '22/06/2022 09:55:11 AM'
         * Playtech.API.formatter.getDateTimeLong(new Date('Wed Jun 22 2022 09:55:11 GMT+0300'));
         */
        getDateTimeLong,
        /**
         * Gets date in 'dateTimeShort' format e.g YYYY-MM-DD HH:MM Depends on Date/Time format setting in CMS.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {*|Date|string} srcDate
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '2022-06-22 09:55'
         * Playtech.API.formatter.getDateTimeShort(new Date('Wed Jun 22 2022 09:55:11 GMT+0300'));
         */
        getDateTimeShort,
        /**
         * Gets date in 'dateTimeLongMs' format e.g YYYY-MM-DD HH:MM:SS.sss Depends on Date/Time format setting in CMS.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {*|Date|string} srcDate
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '2022-02-24 02:40:11.000'
         * Playtech.API.formatter.getDateTimeLongMs(new Date('Wed Feb 24 2022 03:40:11 GMT+0300'));
         */
        getDateTimeLongMs,
        /**
         * Gets date in '12 Apr 2018' format.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {*|Date|string} srcDate
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '5 May 2022'
         * Playtech.API.formatter.getLocaleDateShort('Wed May 05 2022 03:40:11 GMT+0300');
         */
        getLocaleDateShort,
        /**
         * Formats date for export to CSV in 'YYYY-MM-DD' format.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {*|Date|string} srcDate
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '2022-05-05'
         * Playtech.API.formatter.getDateForCsv('Wed May 05 2022 03:40:11 GMT+0300');
         */
        getDateForCsv,
        /**
         * Formats date and time for export to CSV in 'YYYY-MM-DD HH:MM' format.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {*|Date|string} srcDate
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '2022-05-05 03:40'
         * Playtech.API.formatter.getDateTimeForCsv('Wed May 05 2022 03:40:11 GMT+0300');
         */
        getDateTimeForCsv,
        /**
         * Parse date string received from IMS.
         * IMS sends us date in non-standard format ('YYYY-MM-DD HH:MM:SS.sss') without specifying time zone,
         * but according to IMS documentation, this time always comes in GMT.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {string} srcDate
         * @returns {Date}
         * @example
         * // Fri May 06 2022 17:45:21 GMT+0300 (Eastern European Summer Time)
         * Playtech.API.formatter.parseImsDate('2022-05-06 14:45:21.567');
         */
        parseImsDate,
        /**
         * Get date in format 'YYYY-MM-DD HH:MM:SS.sss' in GMT to be sent to IMS (through OGW).
         * @memberOf Playtech.API.formatter
         * @function
         * @param {Date} srcDate
         * @returns {string}
         * @example
         * // '2021-04-14 00:40:11.000'
         * Playtech.API.formatter.getDateForIms(new Date('Wed Apr 14 2021 03:40:11 GMT+0300'));
         */
        getDateForIms,
        /**
         * Parse date string received from getUrlRequest.
         * IMS sends us date in non-standard format ('YYYY-MM-DD+HH%3AMM%3ASS.sss') without specifying time zone,
         * but according to IMS documentation, this time always comes in GMT.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {string} scrDate
         * @returns {Date}
         * @example
         * // Mon May 09 2022 17:45:45 GMT+0300 (Eastern European Summer Time)
         * Playtech.API.formatter.parseImsBankIdDate('2022-05-09+14%3A45%3A45.456');
         */
        parseImsBankIdDate,

        // endregion

        // region Number

        /**
         * Formats number according to options.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {number|string} number Number to format.
         * @param {Object} [options] Format params.
         * @param {number} [options.precision=null] Decimal precision.
         * @param {string} [options.thSeparator=,] {string} Thousand separator.
         * @param {string} [options.decSeparator=.] Decimal separator.
         * @param {string} [locale] If not passed, user's locale is used.
         * @returns {string}
         * @example
         * // '1,234,567,890*00'
         * Playtech.API.formatter.formatNumber('1234567890', {precision: 2 , thSeparator: ',', decSeparator: '*'});
         * @example
         * // '1,234,567,890'
         * Playtech.API.formatter.formatNumber('1234567890');
         */
        formatNumber,
        /**
         * Convert string, that represents localized number, into number if possible.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {string} str Source string.
         * @param {Object} [options] Source format params.
         * @param {string} [options.thSeparator=,] Thousand separator.
         * @param {string} [options.decSeparator=.] Decimal separator.
         * @returns {(* | number | string)}
         * @example
         * // 1234567890
         * Playtech.API.formatter.delocalizeNumber('1,234,567,890*00', {thSeparator: ',', decSeparator: '*'});
         * @example
         * // 'foobar'
         * Playtech.API.formatter.delocalizeNumber('foobar', {thSeparator: ',', decSeparator: '*'});
         */
        delocalizeNumber,

        // endregion

        // region Other

        /**
         * Get duration in 'HH:MM:SS' format without timezone offset.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {Date|string|number} duration
         * @returns {string}
         * @example
         * // '11:39:18'
         * Playtech.API.formatter.getDurationTimeLong(new Date('Wed Jun 12 2022 14:39:18 GMT+0300'));
         */
        getDurationTimeLong,
        /**
         * Get duration in 'HH:MM' format without timezone offset.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {Date|string|number} duration
         * @returns {string}
         * @example
         * // '11:39'
         * Playtech.API.formatter.getDurationTimeShort(new Date('Wed Jun 12 2022 14:39:18 GMT+0300'));
         */
        getDurationTimeShort,
        /**
         * Converts seconds to HH:MM:SS or MM:SS.
         * @memberOf Playtech.API.formatter
         * @function
         * @param {string|number} seconds
         * @returns {string}
         * @example
         * // '01:05'
         * Playtech.API.formatter.formatTimeFromSeconds(65);
         */
        formatTimeFromSeconds

        // endregion
    }), [
        date,
        getDate,
        getTimeLong,
        parseImsDate,
        getTimeShort,
        formatNumber,
        getDateForCsv,
        getDateForIms,
        formatCurrency,
        getDateTimeLong,
        delocalizeNumber,
        getDateTimeShort,
        getDateTimeForCsv,
        getDateTimeLongMs,
        getCurrencySymbol,
        getLocaleDateShort,
        parseImsBankIdDate,
        getDurationTimeLong,
        getDurationTimeShort,
        formatTimeFromSeconds,
        getIsCurrencySymbolAtTheLeft
    ]);
};
